'use client'

import { Professional } from '@/lib/api/common/interfaces/professional.interface'
import {
    getProfessional,
    getProfessionalsByOrganization,
} from '@/lib/api/professionals/professionals-api'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdClose, MdSearch } from 'react-icons/md'
import { FaUserDoctor } from 'react-icons/fa6'
import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'

interface Props {
    value: number | null
    onChange: (
        professionalId: number | null,
        professional: Professional | null
    ) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    size?: 'sm' | 'md'
    allowedIds?: number[]
    priceLabelById?: Record<number, string>
    /**
     * Lista pré-carregada de profissionais (ex: vindos de um catálogo).
     * Quando passado, o componente NÃO faz chamada à API — apenas filtra
     * localmente pelo termo de busca.
     */
    items?: Professional[]
    /**
     * CPF do parceiro escolhido no mesmo atendimento. O profissional com esse documento
     * é o próprio parceiro com outra ficha, e o backend recusa os dois juntos: some da lista.
     */
    excludeDocument?: string | null
}

const cpfDigits = (value?: string | null) => (value ?? '').replace(/\D/g, '')

export function ProfessionalSearchSelector({
    value,
    onChange,
    label = 'Profissional',
    placeholder = 'Buscar profissional...',
    disabled = false,
    className = '',
    size = 'md',
    allowedIds,
    priceLabelById,
    items,
    excludeDocument,
}: Props) {
    const hasItems = Array.isArray(items)
    const { user } = useAuth()
    const [results, setResults] = useState<Professional[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedProfessional, setSelectedProfessional] =
        useState<Professional | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // A rota é `professionals/organization/:organizationId` e o backend rejeita id de outra
    // organização — passar `companyId` aqui dava 403 sempre que os ids não coincidiam, e o
    // `catch` silencioso transformava isso numa lista vazia. Profissional é org-scoped, então
    // esta lista já cobre todas as filiais.
    const loadInitial = useCallback(async () => {
        if (!user?.organizationId) return
        setLoading(true)
        try {
            const response = await getProfessionalsByOrganization(
                user.organizationId,
                { page: 1, limit: 50 }
            )
            if (response.success) {
                setResults(
                    Array.isArray(response.result) ? response.result : []
                )
            }
        } catch {
            // silencioso
        } finally {
            setLoading(false)
        }
    }, [user?.organizationId])

    const searchByTerm = useCallback(
        async (term: string) => {
            if (!user?.organizationId) return
            setLoading(true)
            try {
                const response = await getProfessionalsByOrganization(
                    user.organizationId,
                    {
                        page: 1,
                        limit: 50,
                        search: term || undefined,
                    }
                )
                if (response.success) {
                    setResults(
                        Array.isArray(response.result) ? response.result : []
                    )
                }
            } catch {
                // silencioso
            } finally {
                setLoading(false)
            }
        },
        [user?.organizationId]
    )

    useEffect(() => {
        if (value && !selectedProfessional) {
            if (hasItems) {
                const found = (items || []).find(p => p.id === value)
                if (found) setSelectedProfessional(found)
                return
            }
            if (!user?.companyId) return
            getProfessional(value).then(response => {
                if (response.success && response.result) {
                    setSelectedProfessional(response.result)
                }
            })
        } else if (!value) {
            setSelectedProfessional(null)
        }
    }, [value, selectedProfessional, user?.companyId, hasItems, items])

    useEffect(() => {
        if (!showDropdown) return
        if (hasItems) return // sem chamada à API quando items é fornecido

        if (debounceRef.current) clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            searchByTerm(searchTerm)
        }, 300)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchTerm, showDropdown, searchByTerm, hasItems])

    // Fechar-ao-clicar-fora mora no hook: o painel vai para um portal no body e deixa
    // de estar dentro do container, então o handler antigo o trataria como clique de
    // fora e fecharia antes de a opção ser escolhida.
    const closeDropdown = useCallback(() => setShowDropdown(false), [])
    const { anchorRef, renderPanel } = useAnchoredDropdown(
        showDropdown && !selectedProfessional,
        closeDropdown
    )

    const handleFocus = () => {
        setShowDropdown(true)
        if (hasItems) return
        if (results.length === 0) {
            loadInitial()
        }
    }

    const excludedCpf = cpfDigits(excludeDocument)
    const displayList = (hasItems
        ? (() => {
              const term = searchTerm.trim().toLowerCase()
              if (!term) return items as Professional[]
              return (items as Professional[]).filter(
                  p =>
                      p.name?.toLowerCase().includes(term) ||
                      p.registrationNumber?.toLowerCase().includes(term)
              )
          })()
        : allowedIds
          ? results.filter(p => allowedIds.includes(p.id))
          : results
    ).filter(
        p => excludedCpf.length !== 11 || cpfDigits(p.document) !== excludedCpf
    )

    const handleSelect = (professional: Professional) => {
        setSelectedProfessional(professional)
        onChange(professional.id, professional)
        setShowDropdown(false)
        setSearchTerm('')
    }

    const handleClear = () => {
        setSelectedProfessional(null)
        onChange(null, null)
        setSearchTerm('')
    }

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()
    }

    const getAvatarColor = (id: number) => {
        const colors = [
            'bg-blue-500',
            'bg-green-500',
            'bg-purple-500',
            'bg-orange-500',
            'bg-pink-500',
            'bg-cyan-500',
            'bg-indigo-500',
            'bg-teal-500',
        ]
        return colors[id % colors.length]
    }

    return (
        <div className={`form-control w-full ${className}`} ref={containerRef}>
            {label && (
                <label className="label">
                    <span className="label-text">{label}</span>
                </label>
            )}

            <div className="relative">
                {selectedProfessional ? (
                    <div className="flex items-center gap-2 input input-bordered w-full pr-10">
                        <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${getAvatarColor(selectedProfessional.id)}`}
                        >
                            {getInitials(selectedProfessional.name)}
                        </div>
                        <span className="truncate">
                            {selectedProfessional.name}
                            {selectedProfessional.registrationNumber
                                ? ` - ${selectedProfessional.councilName || ''} ${selectedProfessional.registrationNumber}`
                                : ''}
                        </span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="relative" ref={anchorRef}>
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="text"
                            placeholder={placeholder}
                            className={
                                'input input-bordered w-full pl-10 ' +
                                (size === 'sm' ? 'input-sm!' : '')
                            }
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                            }}
                            onFocus={handleFocus}
                            disabled={disabled}
                            autoComplete="off"
                            aria-autocomplete="none"
                        />
                    </div>
                )}

                {renderPanel(
                    <>
                        {loading ? (
                            <div className="p-4 text-center">
                                <span className="loading loading-spinner loading-sm"></span>
                            </div>
                        ) : displayList.length === 0 ? (
                            <div className="p-4 text-center text-base-content/60 text-sm">
                                <FaUserDoctor className="text-3xl mx-auto mb-2 opacity-50" />
                                <p>
                                    {searchTerm
                                        ? 'Nenhum profissional encontrado'
                                        : 'Nenhum profissional disponível'}
                                </p>
                            </div>
                        ) : (
                            displayList.map(professional => (
                                <button
                                    key={professional.id}
                                    type="button"
                                    className="w-full px-4 py-3 text-left hover:bg-base-200 flex items-center gap-3 border-b border-base-200 last:border-b-0"
                                    onClick={() => handleSelect(professional)}
                                >
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${getAvatarColor(professional.id)}`}
                                    >
                                        {getInitials(professional.name)}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate">
                                            {professional.name}
                                            {priceLabelById?.[professional.id]
                                                ? ` - ${priceLabelById[professional.id]}`
                                                : ''}
                                        </p>
                                        <p className="text-xs text-base-content/60 truncate">
                                            {professional.registrationNumber
                                                ? `${professional.councilName || ''} ${professional.registrationNumber}`
                                                : ''}
                                            {professional.specialties?.length
                                                ? `${professional.registrationNumber ? ' - ' : ''}${professional.specialties.map((s: any) => s.specialty?.name || s.name).join(', ')}`
                                                : professional.formation
                                                  ? `${professional.registrationNumber ? ' - ' : ''}${professional.formation}`
                                                  : ''}
                                        </p>
                                    </div>
                                </button>
                            ))
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
